/**
 * The calibration screen's model (`observer-autogen-plan-projection`, R2–R9;
 * `observer-autogen-calibration-camera-model`, R7–R12;
 * `observer-matching-beyond-geometry-guided-calibration`, R1–R10): the guided spots and traced
 * edges an admin places, which machine corner the screen asks for next, how each tile fits a
 * physical camera, what the readout says, and the stored shape Save writes. Pure, so every rule is
 * tested without rendering; `CameraCalibration` only draws it.
 *
 * The maths is `@repo/front-end-utils`': `fitCameraModel` and its errors, disagreeing pair and
 * marker agreement; `bestNextTarget`, `firstGuessParams` and `projectsInsideView` for the guided
 * target; `calibrationViewRects` and `viewIndexAt` for the tiles; for the overlay,
 * `footprintOutlineInPicture` (the suggestion engine's own machine outline) and
 * `projectPlanOutline` (rooms and areas). Nothing here fits a camera of its own.
 */
import {
  bestNextTarget,
  calibrationViewRects,
  cameraErrors,
  clipPolygonToRect,
  clipPolylineToRect,
  disagreeingPair,
  FIRST_GUESS_VIEW_ALLOWANCE,
  firstGuessParams,
  fitCameraModel,
  fittedHeading,
  footprintCorners,
  footprintOutlineInPicture,
  GUIDED_VIEW_MARGIN,
  isFullCircle,
  isPointVisibleFromCamera,
  isValidCalibration,
  levelToPicture,
  markerAgreement,
  MIN_FIT_PAIRS,
  MODEL_FOOTPRINT_FALLBACK,
  modelFootprintPlanCentre,
  pictureToPlan,
  planProjector,
  planToPicture,
  projectPlanOutline,
  projectsInsideView,
  viewIndexAt,
  type CalibrationTargetPlan,
  type CalibrationViewRect,
  type CameraErrors,
  type CameraModel,
  type FitMarker,
  type MarkerAgreement,
  type ModelFootprintFetchResult,
  type ProjectedOutline,
  type TargetCandidate,
  type VisibilityWall,
} from '@repo/front-end-utils';
import { isModelReference, resolveModelFileName } from '@repo/frontend-types';
import type {
  FloorPlannerCamera,
  FloorPlannerCameraCalibration,
  FloorPlannerCameraCalibrationLayout,
  FloorPlannerCameraCalibrationTarget,
  FloorPlannerItem,
  FloorPlannerSurface,
} from '@repo/frontend-types';

/** Standard (ordinary and wide lenses) or Fisheye (180° and 360°) — R7. */
export type CalibrationLensChoice = 'standard' | 'fisheye';

export type Point = [number, number];

/**
 * One spot: a picture point and the plan point matched to it. A guided spot carries the machine
 * corner it was asked for (`target`); a reopened calibration's pairs may carry none, and may be
 * observer pairs from the retired observer route (still read and fitted on their raised level).
 */
export interface DraftPair {
  id: string;
  /** Picture px (natural, whole frame, y down). */
  picture: Point;
  /** Plan metres (the camera's floor, local coordinates). */
  plan: Point;
  source: 'clicked' | 'observer';
  target?: FloorPlannerCameraCalibrationTarget;
}

/** A traced straight floor edge (Fisheye only): 3 or more picture clicks along something straight. */
export interface DraftEdge {
  id: string;
  points: Point[];
}

export interface CalibrationDraft {
  pairs: DraftPair[];
  edges: DraftEdge[];
  /**
   * `saved`: a stored calibration reopened and shown as fitted; no spot is asked for until
   * Recalibrate starts a guided run (R9). `guided`: the guided run.
   */
  mode: 'guided' | 'saved';
  /** Candidate keys "Can't see it" skipped: never offered again in this session (R2). */
  skipped: string[];
  /**
   * The spots "Add another spot" asked for in all (R4): each press asks for one beyond those placed
   * (or asked for already), however many the run ended on. 0 until pressed; the six per tile apply.
   */
  spotsAsked: number;
  /** The edge being traced, while "Trace a straight edge" is on. */
  tracingEdgeId: string | null;
  /** The spot the keyboard's Delete acts on. */
  selectedPairId: string | null;
  /** Anything changed since it was opened or saved: closing then asks first. */
  dirty: boolean;
  /** Ids are minted from this, never reused within a session. */
  nextId: number;
}

/**
 * Below this share of a tile's diagonal, a pair's picture error is fine; above it the pair is
 * flagged as the likely bad click, and the same bar on the RMS is the poor-fit warning (R7,
 * confirmed by Mike with the design, 2026-10-13).
 */
export const POOR_FIT_RATIO = 0.03;

/** The spots each tile asks for before it can be saved (guided calibration R4). */
export const MIN_GUIDED_SPOTS = 6;

/** A traced edge counts once it has this many points. */
export const MIN_EDGE_POINTS = 3;

/** The fit's camera this far or more from its plan marker is worth moving the marker (R10). */
export const MARKER_SUGGEST_M = 0.5;

const TILE_LETTERS = ['A', 'B', 'C', 'D'];
/** A tile's letter in the readout and the spot chips (row-major). */
export const tileLetter = (index: number): string => TILE_LETTERS[index] ?? String(index + 1);
/** An edge's letter (A, B, …) by its place in the list. */
export const edgeLetter = (index: number): string =>
  index < 26 ? String.fromCharCode(65 + index) : `E${index + 1}`;

const emptyDraft = (): CalibrationDraft => ({
  pairs: [],
  edges: [],
  mode: 'guided',
  skipped: [],
  spotsAsked: 0,
  tracingEdgeId: null,
  selectedPairId: null,
  dirty: false,
  nextId: 1,
});

/**
 * A fresh guided run, or one reopened on the camera's stored calibration (R9): a version-2 one is
 * shown as stored, in `saved` mode. Anything else this code cannot read, a version-1 value
 * included, opens a fresh guided run; the screen warns with `calibrationProblem`.
 */
export const initialDraft = (calibration: unknown): CalibrationDraft => {
  const stored = isValidCalibration(calibration) ? calibration : null;
  if (!stored) return emptyDraft();
  let nextId = 1;
  const pairs: DraftPair[] = [];
  const edges: DraftEdge[] = [];
  for (const view of stored.views) {
    for (const pair of view.pairs) {
      pairs.push({
        id: `p${nextId++}`,
        picture: [pair.picture[0], pair.picture[1]],
        plan: [pair.plan[0], pair.plan[1]],
        source: pair.source,
        ...(pair.target ? { target: { ...pair.target } } : {}),
      });
    }
    for (const edge of view.edges ?? []) {
      edges.push({ id: `e${nextId++}`, points: edge.map(p => [p[0], p[1]] as Point) });
    }
  }
  return { ...emptyDraft(), pairs, edges, mode: 'saved', nextId };
};

export type DraftAction =
  | { type: 'place'; picture: Point; plan: Point; target: FloorPlannerCameraCalibrationTarget }
  | { type: 'skip'; key: string }
  | { type: 'add-spot' }
  | { type: 'move'; id: string; point: Point }
  | { type: 'remove-pair'; id: string }
  | { type: 'select'; id: string | null }
  | { type: 'trace-point'; point: Point }
  | { type: 'toggle-trace' }
  | { type: 'finish-trace' }
  | { type: 'remove-edge'; id: string }
  | { type: 'start-again' }
  | { type: 'saved' };

/** Drop an edge too short to count once tracing it ends. */
const keepEdge = (edge: DraftEdge) => edge.points.length >= 2;

/** The key a machine corner is offered, placed and skipped by. */
export const targetKey = (target: FloorPlannerCameraCalibrationTarget): string =>
  `${target.itemRefid}#${target.corner}`;

/**
 * The spots and edges as the admin edits them (guided calibration R1, R2, R4, R7).
 *
 * - `place` makes a spot from a picture click and the corner the screen asked for.
 * - `skip` sets a corner aside for the rest of the session; `start-again` keeps it set aside.
 * - Only the picture half of a spot moves: the plan half is the corner it was asked for.
 */
export function draftReducer(state: CalibrationDraft, action: DraftAction): CalibrationDraft {
  switch (action.type) {
    case 'place': {
      const id = `p${state.nextId}`;
      const pair: DraftPair = {
        id,
        picture: action.picture,
        plan: action.plan,
        source: 'clicked',
        target: action.target,
      };
      return {
        ...state,
        pairs: [...state.pairs, pair],
        nextId: state.nextId + 1,
        selectedPairId: id,
        dirty: true,
      };
    }
    case 'skip':
      return state.skipped.includes(action.key)
        ? state
        : { ...state, skipped: [...state.skipped, action.key] };
    case 'add-spot':
      return {
        ...state,
        spotsAsked: Math.max(state.spotsAsked, state.pairs.length) + 1,
      };
    case 'move':
      return {
        ...state,
        dirty: true,
        pairs: state.pairs.map(p => (p.id === action.id ? { ...p, picture: action.point } : p)),
      };
    case 'remove-pair':
      return {
        ...state,
        dirty: true,
        pairs: state.pairs.filter(p => p.id !== action.id),
        selectedPairId: state.selectedPairId === action.id ? null : state.selectedPairId,
      };
    case 'select':
      return { ...state, selectedPairId: action.id };
    case 'trace-point':
      if (!state.tracingEdgeId) return state;
      return {
        ...state,
        dirty: true,
        edges: state.edges.map(e =>
          e.id === state.tracingEdgeId ? { ...e, points: [...e.points, action.point] } : e,
        ),
      };
    case 'toggle-trace': {
      if (state.tracingEdgeId) return draftReducer(state, { type: 'finish-trace' });
      const id = `e${state.nextId}`;
      return {
        ...state,
        edges: [...state.edges, { id, points: [] }],
        tracingEdgeId: id,
        nextId: state.nextId + 1,
      };
    }
    case 'finish-trace':
      return {
        ...state,
        tracingEdgeId: null,
        edges: state.edges.filter(e => e.id !== state.tracingEdgeId || keepEdge(e)),
      };
    case 'remove-edge':
      return {
        ...state,
        dirty: true,
        edges: state.edges.filter(e => e.id !== action.id),
        tracingEdgeId: state.tracingEdgeId === action.id ? null : state.tracingEdgeId,
      };
    case 'start-again':
      return {
        ...state,
        pairs: [],
        edges: [],
        mode: 'guided',
        spotsAsked: 0,
        tracingEdgeId: null,
        selectedPairId: null,
        dirty: true,
      };
    case 'saved':
      return { ...state, dirty: false };
    default:
      return state;
  }
}

/** Why a tile has no fit (R4), or that it has one. */
export type ViewVerdict =
  | 'empty'
  | 'too-few'
  | 'near-line'
  | 'under-floor'
  | 'height-out-of-bounds'
  | 'no-convergence'
  | 'ok';

/** The camera this view's pairs are fitted against: its plan marker (R3 priors, R6, R10). */
export interface CalibrationMarker extends FitMarker {
  refid?: string;
}

/** One tile's fit: what it holds, its verdict, and — when `ok` — its camera and errors. */
export interface ViewFit {
  index: number;
  rect: CalibrationViewRect;
  /** The bar for a likely bad click and a poor fit: `POOR_FIT_RATIO` of the tile's diagonal. */
  barPx: number;
  /** The spots whose picture point is in this tile, in the draft's order. */
  pairs: DraftPair[];
  edges: DraftEdge[];
  verdict: ViewVerdict;
  /** The pane a near-line verdict was found on. */
  side: 'picture' | 'plan' | null;
  /** The fitted camera; null without a fit. */
  model: CameraModel | null;
  /** R3a: the fitted level of the observer pairs; null with none, or without a fit. */
  observerHeight: number | null;
  /** Each pair's error in picture px and floor metres (R5); null without a fit. */
  errors: CameraErrors | null;
  /** R5: the pair whose removal lowers the RMS most; null with fewer than five pairs. */
  disagreeingPairId: string | null;
  /** R6: how far the fit puts the camera from its marker; null without a fit. */
  marker: MarkerAgreement | null;
  /** The likely bad click: the worst pair, when its error is over the bar. */
  badPairIds: string[];
  /** The RMS is over the bar. */
  poor: boolean;
}

/**
 * The last fit of each tile and the inputs it was made from, so a refit redoes only the tiles whose
 * pairs, edges or marker changed (`fitViews`), warm-started from the tile's last fit. Keep one per
 * screen.
 */
export type ViewFitCache = Map<number, { key: string; fit: ViewFit }>;

export const createViewFitCache = (): ViewFitCache => new Map();

/**
 * The fit cache a reopened calibration starts from (R9): each tile holds the camera stored for it,
 * so the reopened fit is warm-started from the stored solution and stays at it, rather than a cold
 * twelve-heading search that may settle on another one. Seeded only for a readable value whose
 * tiles are its layout's own; a tile with no stored camera, or of another lens, is left cold. The
 * seed's key matches no fit, so the first fit of every tile is made, from the stored camera.
 */
export function viewFitCacheFrom(calibration: unknown): ViewFitCache {
  const cache = createViewFitCache();
  if (!isValidCalibration(calibration)) return cache;
  const { layout, lensModel, frameWidth, frameHeight, views } = calibration;
  const rects = calibrationViewRects(layout, frameWidth, frameHeight);
  const lined =
    rects.length === views.length &&
    views.every((view, k) => view.rect.every((v, i) => Math.abs(v - rects[k][i]) < 0.5));
  if (!lined) return cache;
  views.forEach((view, index) => {
    if (!view.camera || view.camera.lens !== lensModel) return;
    cache.set(index, {
      key: '',
      fit: {
        index,
        rect: rects[index],
        barPx: POOR_FIT_RATIO * Math.hypot(rects[index][2], rects[index][3]),
        pairs: [],
        edges: [],
        verdict: 'ok',
        side: null,
        model: { ...view.camera },
        observerHeight: view.observerHeight,
        errors: null,
        disagreeingPairId: null,
        marker: null,
        badPairIds: [],
        poor: false,
      },
    });
  });
  return cache;
}

/** Everything a tile's fit depends on, as a string: equal strings, equal fits. */
const fitKey = (
  rect: CalibrationViewRect,
  lens: CalibrationLensChoice,
  layout: FloorPlannerCameraCalibrationLayout,
  marker: CalibrationMarker,
  pairs: ReadonlyArray<DraftPair>,
  edges: ReadonlyArray<DraftEdge>,
) =>
  JSON.stringify([
    rect,
    lens,
    layout,
    [marker.x, marker.y, marker.rotation, marker.fovAngle],
    pairs.map(p => [p.id, p.picture, p.plan, p.source]),
    edges.map(e => [e.id, e.points]),
  ]);

/**
 * Fit every tile from the draft (R2a, R3–R6). Pairs are grouped by the tile their picture point is in
 * (`viewIndexAt`). A traced edge is split the same way: each tile uses only the edge's points that lie
 * in it, and only Fisheye uses edges. Each tile fits its own camera against the plan marker; with a
 * `cache`, a tile whose inputs did not change keeps its last fit, and one whose inputs did is
 * refitted from its last fitted camera (one start, not twelve: guided calibration, fit cost).
 */
export function fitViews(
  draft: Pick<CalibrationDraft, 'pairs' | 'edges'> & {
    layout: FloorPlannerCameraCalibrationLayout;
    lens: CalibrationLensChoice;
  },
  frame: { width: number; height: number },
  marker: CalibrationMarker,
  cache?: ViewFitCache,
): ViewFit[] {
  const rects = calibrationViewRects(draft.layout, frame.width, frame.height);
  const tileOf = (p: Point) => viewIndexAt(rects, { x: p[0], y: p[1] });
  const edges = draft.lens === 'fisheye' ? draft.edges : [];

  return rects.map((rect, index) => {
    const pairs = draft.pairs.filter(p => tileOf(p.picture) === index);
    const viewEdges = edges
      .map(e => ({ id: e.id, points: e.points.filter(p => tileOf(p) === index) }))
      .filter(e => e.points.length > 0);
    if (!cache) return fitView(rect, index, pairs, viewEdges, draft, marker, null);
    const key = fitKey(rect, draft.lens, draft.layout, marker, pairs, viewEdges);
    const kept = cache.get(index);
    if (kept && kept.key === key) return kept.fit;
    // Warm-start from the tile's last fitted camera of the same lens, and its observer level.
    const start =
      kept?.fit.model && kept.fit.model.lens === draft.lens
        ? { model: kept.fit.model, observerHeight: kept.fit.observerHeight }
        : null;
    const fit = fitView(rect, index, pairs, viewEdges, draft, marker, start);
    cache.set(index, { key, fit });
    return fit;
  });
}

/** One tile's fit (see `fitViews`): from `start` alone when given, else the twelve headings. */
function fitView(
  rect: CalibrationViewRect,
  index: number,
  pairs: DraftPair[],
  viewEdges: DraftEdge[],
  draft: { layout: FloorPlannerCameraCalibrationLayout; lens: CalibrationLensChoice },
  marker: CalibrationMarker,
  start: { model: CameraModel; observerHeight: number | null } | null,
): ViewFit {
  const base: ViewFit = {
    index,
    rect,
    barPx: POOR_FIT_RATIO * Math.hypot(rect[2], rect[3]),
    pairs,
    edges: viewEdges,
    verdict: pairs.length === 0 ? 'empty' : 'too-few',
    side: null,
    model: null,
    observerHeight: null,
    errors: null,
    disagreeingPairId: null,
    marker: null,
    badPairIds: [],
    poor: false,
  };
  if (pairs.length < MIN_FIT_PAIRS) return base;
  const input = {
    rect,
    lens: draft.lens,
    layout: draft.layout,
    pairs: pairs.map(p => ({ picture: p.picture, plan: p.plan, source: p.source })),
    edges: viewEdges.filter(e => e.points.length >= MIN_EDGE_POINTS).map(e => e.points),
    marker,
  };
  const fit = fitCameraModel(
    input,
    start ? { starts: [start.model], observerHeight: start.observerHeight } : {},
  );
  if (fit.verdict === 'near-line') return { ...base, verdict: 'near-line', side: fit.side };
  if (fit.verdict !== 'ok') return { ...base, verdict: fit.verdict };
  const errors = cameraErrors(fit.model, input.pairs, fit.observerHeight, rect);
  const disagreeing = disagreeingPair(input, fit);
  // One likely bad click: the worst pair, when it is over the bar.
  const badPairIds =
    errors.worstPxIndex >= 0 && errors.worstPx > base.barPx ? [pairs[errors.worstPxIndex].id] : [];
  return {
    ...base,
    verdict: 'ok',
    model: fit.model,
    observerHeight: fit.observerHeight,
    errors,
    disagreeingPairId: disagreeing === null ? null : pairs[disagreeing].id,
    marker: markerAgreement(fit.model, marker),
    badPairIds,
    poor: errors.rmsPx > base.barPx,
  };
}

/** Guided calibration R4: a view saves once it has an acceptable fit on six spots or more. */
export const isSaveable = (fit: ViewFit): boolean =>
  fit.verdict === 'ok' && fit.pairs.length >= MIN_GUIDED_SPOTS;

/** The calibration can be saved once every view is saveable (guided calibration R5). */
export const canSave = (fits: ReadonlyArray<ViewFit>): boolean =>
  fits.length > 0 && fits.every(isSaveable);

/**
 * The readout's tone, which `CALIBRATION_LOOK.readout` draws in a status role: `idle` no spots yet
 * (neutral), `exact` spots still being placed (work to do), `good` fitted and saveable (all done),
 * `warn` a fit the points cannot give (problem), `bad` a poor fit or a likely bad click (problem).
 */
export type ReadoutTone = 'idle' | 'exact' | 'good' | 'warn' | 'bad';

export interface Readout {
  tone: ReadoutTone;
  /** What the readout line says. */
  text: string;
  /** What the polite region says for it: a refusal's sentence, or the fit. */
  announce: string;
}

/** "1 pair", "2 pairs". */
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A spot's number on screen: its place in the draft's list, from 1. */
export const pairNumber = (pairs: ReadonlyArray<DraftPair>, id: string): number =>
  pairs.findIndex(p => p.id === id) + 1;

const metres = (m: number) => `${m.toFixed(2)} m`;
const lensWord = (lens: CalibrationLensChoice) => (lens === 'fisheye' ? 'Fisheye' : 'Standard');

/**
 * R9's fitted line: `Fisheye · 7 spots · median 0.21 m, worst 0.64 m (spot 3) · 31 px RMS`, with
 * the metres left out when no pair's picture point reaches the floor.
 */
function fittedLine(
  fit: ViewFit,
  e: CameraErrors,
  draft: Pick<CalibrationDraft, 'pairs'>,
  lens: CalibrationLensChoice,
): string {
  const metresPart =
    e.medianM !== null && e.worstM !== null && e.worstMIndex >= 0
      ? ` · median ${metres(e.medianM)}, worst ${metres(e.worstM)} (spot ${pairNumber(
          draft.pairs,
          fit.pairs[e.worstMIndex].id,
        )})`
      : '';
  return `${lensWord(lens)} · ${plural(fit.pairs.length, 'spot')}${metresPart} · ${Math.round(
    e.rmsPx,
  )} px RMS`;
}

/** "Fit: median {m} m, worst {w} m, spot {n} disagrees with the others". */
function fitAnnouncement(
  fit: ViewFit,
  e: CameraErrors,
  draft: Pick<CalibrationDraft, 'pairs'>,
): string {
  const parts = [
    e.medianM !== null && e.worstM !== null
      ? `Fit: median ${metres(e.medianM)}, worst ${metres(e.worstM)}`
      : `Fit: ${Math.round(e.rmsPx)} px RMS`,
  ];
  if (fit.disagreeingPairId) {
    parts.push(`spot ${pairNumber(draft.pairs, fit.disagreeingPairId)} disagrees with the others`);
  }
  return parts.join(', ');
}

/** The refusal sentences (R4), by verdict: the readout's line and what is announced. */
export function refusalSentence(verdict: ViewVerdict): { text: string; announce: string } | null {
  switch (verdict) {
    case 'near-line':
      return {
        text: 'Can’t fit yet — points too close to a line',
        announce: 'Points are too close to a line',
      };
    case 'under-floor': {
      const text = 'Can’t fit — these points put the camera under the floor';
      return { text, announce: text };
    }
    case 'height-out-of-bounds': {
      const text = 'Can’t fit — these points put the camera outside 1.5–10 m up';
      return { text, announce: text };
    }
    case 'no-convergence': {
      const text = 'Can’t fit these points — move one and try again';
      return { text, announce: text };
    }
    default:
      return null;
  }
}

/** One tile's state in a word or two, for the four-tile readout and the tile labels. */
export function tileSummary(fit: ViewFit): { text: string; tone: ReadoutTone } {
  switch (fit.verdict) {
    case 'ok':
      if (fit.poor) return { text: 'poor fit', tone: 'bad' };
      if (isSaveable(fit)) return { text: 'fitted', tone: 'good' };
      return { text: `${fit.pairs.length} of ${MIN_GUIDED_SPOTS}`, tone: 'exact' };
    case 'empty':
      return { text: 'no spots', tone: 'idle' };
    case 'too-few':
      return { text: `${fit.pairs.length} of ${MIN_GUIDED_SPOTS}`, tone: 'exact' };
    case 'near-line':
      return { text: 'points too close to a line', tone: 'warn' };
    default:
      return { text: "can't fit", tone: 'warn' };
  }
}

/**
 * The readout line for a single view (R9). A refusal replaces the fitted line; so does being short
 * of six spots.
 */
function singleReadout(
  fit: ViewFit,
  draft: Pick<CalibrationDraft, 'pairs'>,
  lens: CalibrationLensChoice,
): Readout {
  const n = fit.pairs.length;
  const refusal = refusalSentence(fit.verdict);
  if (refusal) return { tone: 'warn', ...refusal };
  switch (fit.verdict) {
    case 'empty':
    case 'too-few': {
      const text =
        n === 0 && draft.pairs.length === 0
          ? `Place ${MIN_GUIDED_SPOTS} spots to save`
          : `${n} of ${MIN_GUIDED_SPOTS} spots`;
      return { tone: n === 0 ? 'idle' : 'exact', text, announce: text };
    }
    case 'ok':
    default: {
      const e = fit.errors;
      if (!e) {
        // An `ok` fit always carries its errors; anything else reads as a fit that did not settle.
        const text = 'Can’t fit these points — move one and try again';
        return { tone: 'warn', text, announce: text };
      }
      const announce = fitAnnouncement(fit, e, draft);
      if (n < MIN_GUIDED_SPOTS) {
        return { tone: 'exact', text: `${n} of ${MIN_GUIDED_SPOTS} spots`, announce };
      }
      return {
        tone: fit.poor || fit.badPairIds.length > 0 ? 'bad' : 'good',
        text: fittedLine(fit, e, draft, lens),
        announce,
      };
    }
  }
}

/** The readout for the whole screen: a single view's line, or the four tiles summarised (R2a). */
export function readoutFor(
  fits: ReadonlyArray<ViewFit>,
  draft: Pick<CalibrationDraft, 'pairs'>,
  lens: CalibrationLensChoice,
): Readout {
  if (fits.length === 1) return singleReadout(fits[0], draft, lens);
  const parts = fits.map(f => {
    const letter = tileLetter(f.index);
    if (f.verdict === 'too-few' || (f.verdict === 'ok' && !f.poor && !isSaveable(f))) {
      return `${letter} needs ${MIN_GUIDED_SPOTS - f.pairs.length} more`;
    }
    if (f.verdict === 'empty') return `${letter} empty`;
    return `${letter} ${tileSummary(f).text}`;
  });
  const text = `${parts.join(' · ')}${canSave(fits) ? ' — you can save now' : ''}`;
  const tone: ReadoutTone = fits.some(f => f.verdict === 'ok' && f.poor)
    ? 'bad'
    : fits.some(f => refusalSentence(f.verdict) !== null)
      ? 'warn'
      : canSave(fits)
        ? 'good'
        : fits.every(f => f.verdict === 'empty')
          ? 'idle'
          : 'exact';
  return { tone, text: `Tile ${text}`, announce: `Tile ${text}` };
}

/** The note on the pair that disagrees with the others (R9). */
export const DISAGREE_NOTE =
  'doesn’t agree with the others — re-click it, or check this spot on the plan';

/**
 * R10: when the fit puts the camera `MARKER_SUGGEST_M` or more from its plan marker, the absolute
 * position to move the marker to — and, for a camera that is not 360°, the fitted heading. Null
 * otherwise. Absolute, so pressing twice writes the same values.
 */
export function markerSuggestion(
  fit: Pick<ViewFit, 'verdict' | 'model' | 'marker'>,
  camera: { fovAngle: number },
): { distanceM: number; patch: { x: number; y: number; rotation?: number } } | null {
  if (fit.verdict !== 'ok' || !fit.model || !fit.marker) return null;
  if (fit.marker.distanceM < MARKER_SUGGEST_M) return null;
  const patch: { x: number; y: number; rotation?: number } = {
    x: roundTo(fit.model.x, 3),
    y: roundTo(fit.model.y, 3),
  };
  if (!isFullCircle(camera.fovAngle)) patch.rotation = roundTo(fittedHeading(fit.model), 1);
  return { distanceM: fit.marker.distanceM, patch };
}

/**
 * The saved source (R4): `observers` with no clicked pair, `mixed` with both. Only a reopened
 * calibration from the retired observer route can still hold observer pairs.
 */
export function calibrationSource(
  pairs: ReadonlyArray<DraftPair>,
): FloorPlannerCameraCalibration['source'] {
  const observers = pairs.some(p => p.source === 'observer');
  const clicked = pairs.some(p => p.source === 'clicked');
  if (observers && !clicked) return 'observers';
  return observers ? 'mixed' : 'clicked';
}

function roundTo(n: number, places: number) {
  const f = 10 ** places;
  return Math.round(n * f) / f;
}

const roundModel = (m: CameraModel): CameraModel => ({
  x: roundTo(m.x, 4),
  y: roundTo(m.y, 4),
  height: roundTo(m.height, 4),
  yaw: roundTo(m.yaw, 4),
  pitch: roundTo(m.pitch, 4),
  roll: roundTo(m.roll, 4),
  f: roundTo(m.f, 3),
  cx: m.cx,
  cy: m.cy,
  k1: roundTo(m.k1, 6),
  k2: roundTo(m.k2, 6),
  lens: m.lens,
});

const roundOrNull = (n: number | null | undefined, places: number) =>
  n === null || n === undefined || !Number.isFinite(n) ? null : roundTo(n, places);

/**
 * The stored calibration, version 2 (R13), from the draft and its fits: every tile, with its spots
 * (each guided spot with its `target`) and edges, and its camera and errors when it is saveable,
 * `camera: null` otherwise (Failure States, Partial). Edges are stored with Fisheye only.
 */
export function buildCalibration({
  draft,
  fits,
  layout,
  lens,
  rtspEndpointBid,
  frame,
  fittedAt,
}: {
  draft: CalibrationDraft;
  fits: ReadonlyArray<ViewFit>;
  layout: FloorPlannerCameraCalibrationLayout;
  lens: CalibrationLensChoice;
  rtspEndpointBid: string;
  frame: { width: number; height: number };
  fittedAt: string;
}): FloorPlannerCameraCalibration {
  return {
    version: 2,
    rtspEndpointBid,
    frameWidth: frame.width,
    frameHeight: frame.height,
    layout,
    lensModel: lens,
    views: fits.map(fit => {
      const saved =
        isSaveable(fit) && fit.model && fit.errors
          ? { model: fit.model, errors: fit.errors }
          : null;
      return {
        rect: [...fit.rect] as [number, number, number, number],
        pairs: fit.pairs.map(p => ({
          picture: [roundTo(p.picture[0], 2), roundTo(p.picture[1], 2)] as Point,
          plan: [roundTo(p.plan[0], 4), roundTo(p.plan[1], 4)] as Point,
          source: p.source,
          ...(p.target
            ? { target: { itemRefid: p.target.itemRefid, corner: p.target.corner } }
            : {}),
        })),
        camera: saved ? roundModel(saved.model) : null,
        rmsErrorPx: saved ? roundOrNull(saved.errors.rmsPx, 2) : null,
        rmsErrorM: saved ? roundOrNull(saved.errors.rmsM, 4) : null,
        worstErrorM: saved ? roundOrNull(saved.errors.worstM, 4) : null,
        observerHeight: saved ? roundOrNull(fit.observerHeight, 4) : null,
        edges:
          lens === 'fisheye'
            ? fit.edges
                .filter(e => e.points.length >= MIN_EDGE_POINTS)
                .map(e => e.points.map(p => [roundTo(p[0], 2), roundTo(p[1], 2)] as Point))
            : [],
      };
    }),
    source: calibrationSource(draft.pairs),
    fittedAt,
  };
}

/**
 * The overlay, as SVG path data in natural picture px (R8). Repainted when a fit, the plan or the
 * machine footprints change — never on zoom or pan.
 */
export interface CalibrationOverlay {
  rooms: string;
  machines: string;
  /** Each pair's error: from where it was clicked to where its plan point projects. */
  residuals: string;
  /** Where each fitted pair's plan point projects, for a dot. */
  projected: Array<{ id: string; x: number; y: number }>;
  /** The tile boundaries, on a four-tile picture. */
  tiles: string;
  /**
   * Machines drawn at the stand-in size (`MODEL_FOOTPRINT_FALLBACK`), once each however many
   * tiles see them: no model set, or a model whose header could not be read. Zero until settled.
   */
  standIns: { noModel: number; unreadable: number };
  /**
   * The traced edges (Fisheye only). In a fitted tile, each is drawn as the fitted camera sees the
   * straight floor line through its points (curved through the lens); before a fit, as clicked.
   */
  edges: string;
}

const pathOf = (points: ReadonlyArray<{ x: number; y: number }>, close: boolean) =>
  points.length === 0
    ? ''
    : `M ${points.map(p => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' L ')}${close ? ' Z' : ''}`;

/**
 * The calibration screen's model footprints (`onboarding-3d-plan-footprint-calibration`, R3–R4):
 * one header read per model, keyed by `modelFootprintKey`. `settled` once every model has
 * answered; until then no machine outline is drawn.
 */
export interface MachineFootprints {
  settled: boolean;
  footprints: ReadonlyMap<string, ModelFootprintFetchResult>;
}

/**
 * The one key a model is read and looked up by: the file name launch and the model share use,
 * lower-cased, so `Pin_selected+Chest_press.glb` and `pin_selected+chest_press` are one read.
 * Null for no model — empty, blank, or a reference launch would drop (`planItemToMachineEntity`
 * screens with `isModelReference`, so the live twin draws no model for it either).
 */
export function modelFootprintKey(reference: string | null | undefined): string | null {
  const trimmed = reference?.trim();
  if (!trimmed || !isModelReference(trimmed)) return null;
  return resolveModelFileName(trimmed).toLowerCase();
}

/**
 * A machine's floor outline in plan metres: the live twin's box when its model was read (centred
 * by the shared rule, at its saved rotation), otherwise the shared stand-in centred on its point.
 */
function machineOutline(
  item: FloorPlannerItem,
  { footprints }: MachineFootprints,
): { corners: ReturnType<typeof footprintCorners>; standIn: 'noModel' | 'unreadable' | null } {
  const key = modelFootprintKey(item.modelFile);
  const read = key ? footprints.get(key) : undefined;
  const footprint = read?.ok ? read.footprint : MODEL_FOOTPRINT_FALLBACK;
  const corners = footprintCorners(
    { ...item, ...modelFootprintPlanCentre(item, footprint) },
    { width: footprint.widthM, depth: footprint.depthM },
  );
  return { corners, standIn: read?.ok ? null : key ? 'unreadable' : 'noModel' };
}

/** A floor point into the picture through the tile's fitted camera (axes worked out once). */
const floorProjector = (model: CameraModel) => planProjector(model);

/**
 * A projected outline as path data, cut to its tile: a closed ring clipped as a polygon, the open
 * paths a lens's edge leaves clipped as lines, so nothing is drawn across the gap.
 */
const outlinePaths = (outline: ProjectedOutline, rect: CalibrationViewRect): string[] => {
  if (outline.closed) {
    const ring = outline.paths[0] ?? [];
    const clipped = ring.length >= 3 ? clipPolygonToRect(ring, rect) : [];
    return clipped.length >= 3 ? [pathOf(clipped, true)] : [];
  }
  return outline.paths.flatMap(path =>
    clipPolylineToRect(path, rect).map(part => pathOf(part, false)),
  );
};

/**
 * A traced edge as the fitted camera sees it (R8): its first and last points taken to the floor, and
 * the straight floor line between them projected back, sampled so the lens curves it. Null when an
 * end does not reach the floor.
 */
export function edgeGuide(
  points: ReadonlyArray<Point>,
  model: CameraModel,
): Array<{ x: number; y: number }> | null {
  if (points.length < 2) return null;
  const a = pictureToPlan(model, { x: points[0][0], y: points[0][1] });
  const b = pictureToPlan(model, {
    x: points[points.length - 1][0],
    y: points[points.length - 1][1],
  });
  if (!a || !b) return null;
  const samples = 24;
  const out: Array<{ x: number; y: number }> = [];
  for (let k = 0; k <= samples; k++) {
    const t = k / samples;
    const q = planToPicture(model, { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) });
    if (q) out.push(q);
  }
  return out.length >= 2 ? out : null;
}

/**
 * Every live machine's footprint and every room and area outline on the floor, through each fitted
 * tile's camera and clipped to that tile (R8, R14). Not limited to the sight cone, so the admin sees
 * where a fit goes wrong beyond it.
 *
 * A machine is drawn with the suggestion engine's own outline (`footprintOutlineInPicture`,
 * `observer-matching-beyond-geometry-calibration-outline-match`): its footprint sampled every 25 cm,
 * the samples that project wrapped in their convex hull, cut to the tile, so the screen shows the
 * shape matching uses. Rooms and areas are sampled and curved by the lens, and split — never
 * bridged — where they leave the camera's view.
 */
export function overlayFor(
  fits: ReadonlyArray<ViewFit>,
  items: ReadonlyArray<FloorPlannerItem>,
  surfaces: ReadonlyArray<FloorPlannerSurface>,
  machineFootprints: MachineFootprints,
): CalibrationOverlay {
  // Resolved once per machine, not per tile, so a machine seen by two tiles is counted once.
  const outlines = machineFootprints.settled
    ? items.filter(item => !item._deleted).map(item => machineOutline(item, machineFootprints))
    : [];
  const standIns = { noModel: 0, unreadable: 0 };
  for (const { standIn } of outlines) if (standIn) standIns[standIn] += 1;
  const rooms: string[] = [];
  const machines: string[] = [];
  const residuals: string[] = [];
  const edges: string[] = [];
  const projected: CalibrationOverlay['projected'] = [];
  for (const fit of fits) {
    for (const edge of fit.edges) {
      const guide = fit.model ? edgeGuide(edge.points, fit.model) : null;
      if (guide) edges.push(pathOf(guide, false));
      else if (edge.points.length >= 2) {
        edges.push(
          pathOf(
            edge.points.map(([x, y]) => ({ x, y })),
            false,
          ),
        );
      }
    }
    if (fit.verdict !== 'ok' || !fit.model) continue;
    const project = floorProjector(fit.model);
    for (const { corners } of outlines) {
      // The suggestion engine's own outline, so the screen shows the shape matching uses.
      const ring = footprintOutlineInPicture(corners, project, fit.rect);
      if (ring.length >= 3) machines.push(pathOf(ring, true));
    }
    for (const surface of surfaces) {
      if ((surface as { _deleted?: boolean })._deleted || surface.poly.length < 3) continue;
      const outline = projectPlanOutline(
        surface.poly.map(p => [p.x, p.y] as const),
        project,
      );
      rooms.push(...outlinePaths(outline, fit.rect));
    }
    for (const pair of fit.pairs) {
      // R3a: an observer pair is a point on its level, not the floor.
      const z = pair.source === 'observer' ? (fit.observerHeight ?? 0) : 0;
      const q = levelToPicture(fit.model, { x: pair.plan[0], y: pair.plan[1] }, z);
      if (!q) continue;
      projected.push({ id: pair.id, ...q });
      if (Math.hypot(q.x - pair.picture[0], q.y - pair.picture[1]) >= 1) {
        residuals.push(pathOf([{ x: pair.picture[0], y: pair.picture[1] }, q], false));
      }
    }
  }
  const tiles =
    fits.length > 1
      ? fits
          .filter(f => f.rect[0] > 0 || f.rect[1] > 0)
          .map(f => {
            const [x, y, w, h] = f.rect;
            // A tile's left and top edges are the splits: draw each once.
            return [
              x > 0 ? `M ${x} ${y} L ${x} ${y + h}` : '',
              y > 0 ? `M ${x} ${y} L ${x + w} ${y}` : '',
            ]
              .filter(Boolean)
              .join(' ');
          })
          .join(' ')
      : '';
  return {
    rooms: rooms.join(' '),
    machines: machines.join(' '),
    residuals: residuals.join(' '),
    projected,
    tiles,
    edges: edges.join(' '),
    standIns,
  };
}

/** A camera's effective field of view: its own, or the default preset's 100° (`effectiveCoverage`). */
export const fieldOfView = (camera: Pick<FloorPlannerCamera, 'fovAngle'>): number =>
  typeof camera.fovAngle === 'number' && Number.isFinite(camera.fovAngle) && camera.fovAngle > 0
    ? camera.fovAngle
    : 100;

/** The plan marker a camera's fit starts from and is judged against (R3, R6). */
export const markerOf = (
  camera: Pick<FloorPlannerCamera, 'refid' | 'x' | 'y' | 'rotation' | 'fovAngle'>,
): CalibrationMarker => ({
  refid: camera.refid,
  x: camera.x,
  y: camera.y,
  rotation: camera.rotation ?? 0,
  fovAngle: fieldOfView(camera),
});

/**
 * The lens and layout a guided run uses (guided calibration R5), chosen for the admin and never
 * changed on screen. The lens is Fisheye for a camera whose plan coverage is 180° or more, or whose
 * saved calibration is Fisheye; otherwise Standard. The layout is four tiles for a full-circle
 * camera (360°), otherwise a single view.
 */
export function guidedSetup(camera: Pick<FloorPlannerCamera, 'fovAngle' | 'calibration'>): {
  layout: FloorPlannerCameraCalibrationLayout;
  lens: CalibrationLensChoice;
} {
  const fov = fieldOfView(camera);
  const stored = isValidCalibration(camera.calibration) ? camera.calibration.lensModel : null;
  return {
    layout: isFullCircle(fov) ? 'grid-2x2' : 'single',
    lens: stored === 'fisheye' || fov >= 180 ? 'fisheye' : 'standard',
  };
}

/** A machine corner the guided screen can ask for (R3): a footprint corner of a read model. */
export interface GuidedCorner extends TargetCandidate {
  itemRefid: string;
  corner: 0 | 1 | 2 | 3;
  /** What the announcement calls the machine ("Treadmill 4"). */
  name: string;
  /** False when `guidedCorners`' sight check finds it out of the camera's reach or behind a wall. */
  inSight?: boolean;
}

const footprintOfItem = (
  item: Pick<FloorPlannerItem, 'modelFile'>,
  footprints: ReadonlyMap<string, ModelFootprintFetchResult>,
) => {
  const key = modelFootprintKey(item.modelFile);
  const read = key ? footprints.get(key) : undefined;
  return read?.ok ? read.footprint : null;
};

/**
 * The corners the guided screen may ask for (R3): the four `footprintCorners` of every live machine
 * on the floor whose model footprint was read, at its live box centre (`modelFootprintPlanCentre`).
 * A machine with no model, or one whose model could not be read, offers none: the 1.0 × 1.2 m
 * stand-in is a guess, not a corner a person can find.
 *
 * With `sight`, each corner says whether it is within the camera's reach with no wall between, by the
 * plan cone's own rule (`isPointVisibleFromCamera`), and `guidedTarget` offers those first. The
 * angle is not applied: the marker's heading is what calibration corrects. Without this a far corner
 * lands at the picture's top edge, inside the first guess's allowance (FTC-RED's Cardio camera
 * asked for a bench 35 m away).
 */
export function guidedCorners(
  items: ReadonlyArray<FloorPlannerItem>,
  footprints: ReadonlyMap<string, ModelFootprintFetchResult>,
  sight?: { camera: FloorPlannerCamera; walls: ReadonlyArray<VisibilityWall> },
): GuidedCorner[] {
  const all = sight ? { ...sight.camera, fovAngle: 360 } : null;
  const out: GuidedCorner[] = [];
  for (const item of items) {
    if (item._deleted) continue;
    const footprint = footprintOfItem(item, footprints);
    if (!footprint) continue;
    const corners = footprintCorners(
      { ...item, ...modelFootprintPlanCentre(item, footprint) },
      { width: footprint.widthM, depth: footprint.depthM },
    );
    corners.forEach(([x, y], k) => {
      const corner = k as 0 | 1 | 2 | 3;
      out.push({
        ...(all && sight ? { inSight: isPointVisibleFromCamera(all, sight.walls, { x, y }) } : {}),
        key: targetKey({ itemRefid: item.refid, corner }),
        plan: { x, y },
        itemRefid: item.refid,
        corner,
        name: item.name?.trim() || 'a machine',
      });
    });
  }
  return out;
}

/**
 * The out-of-date rule's view of the plan (guided calibration R10) from the screen's or the panel's
 * footprint reads: a machine whose model was not read is not checked.
 */
export const targetPlanOf = (
  items: ReadonlyArray<FloorPlannerItem>,
  footprints: ReadonlyMap<string, ModelFootprintFetchResult>,
): CalibrationTargetPlan => ({
  items,
  footprintOf: item => footprintOfItem(item, footprints),
});

/** What the guided screen is doing (R1–R4, Inputs and Failure States). */
export type GuidedStatus =
  /** A stored calibration is shown; Recalibrate starts a guided run. */
  | 'saved'
  /** Machine sizes are still being read: no target yet. */
  | 'reading'
  /** No machine on the plan has a readable size: nothing to match. */
  | 'nothing'
  /** Every candidate is placed, skipped or out of view. */
  | 'exhausted'
  /** Every spot asked for is placed: Save, or Add another spot. */
  | 'done'
  /** A target is offered. */
  | 'asking';

export interface GuidedTarget extends GuidedCorner {
  /** The tile it is asked for in. */
  view: number;
  /**
   * Where the tile's estimate (its fit, or its first guess) puts the corner on the picture,
   * whole-frame px; null when the estimate cannot project it (findable-spots R3).
   */
  expected: { x: number; y: number } | null;
  /** The estimate is the tile's fit, not its first guess: _should be_ rather than _probably_. */
  fitted: boolean;
  /** The person picked this corner on the plan (findable-spots R4). */
  chosen: boolean;
}

export interface GuidedState {
  status: GuidedStatus;
  /** The corner asked for, with the tile it is asked for in; null unless `asking`. */
  target: GuidedTarget | null;
  /** The spots asked for in all: six per tile, plus one per Add another spot. */
  wanted: number;
}

/**
 * The tile's first guess (R3): the marker's, through `firstGuessParams`. On four tiles each tile is
 * assumed to look a quarter turn on from the one before, row-major, starting at the marker's
 * rotation: only a first guess, which the tile's own fit replaces from four spots.
 */
const tileFirstGuess = (
  marker: CalibrationMarker,
  rect: CalibrationViewRect,
  lens: CalibrationLensChoice,
  layout: FloorPlannerCameraCalibrationLayout,
  index: number,
) =>
  firstGuessParams(
    { ...marker, rotation: marker.rotation + (layout === 'grid-2x2' ? 90 * index : 0) },
    rect,
    lens,
  );

/**
 * The corners no longer open (findable-spots R4.1): skipped, or the target of a placed spot. One
 * home for the rule, so a plan pick and the chooser agree on what is open.
 */
export const takenKeys = (draft: Pick<CalibrationDraft, 'pairs' | 'skipped'>): Set<string> =>
  new Set([...draft.skipped, ...draft.pairs.flatMap(p => (p.target ? [targetKey(p.target)] : []))]);

/** One of nine equal parts of a tile, the words the instruction uses (findable-spots R3). */
export type PictureRegion =
  | 'top left'
  | 'top'
  | 'top right'
  | 'left'
  | 'middle'
  | 'right'
  | 'bottom left'
  | 'bottom'
  | 'bottom right';

const REGION_GRID: ReadonlyArray<ReadonlyArray<PictureRegion>> = [
  ['top left', 'top', 'top right'],
  ['left', 'middle', 'right'],
  ['bottom left', 'bottom', 'bottom right'],
];

/**
 * The part of the tile a picture point lies in, by equal thirds; a point outside the tile names the
 * nearest part (findable-spots R3.3).
 */
export function pictureRegion(
  point: { x: number; y: number },
  rect: CalibrationViewRect,
): PictureRegion {
  const [rx, ry, w, h] = rect;
  const third = (v: number, origin: number, size: number) =>
    Math.min(2, Math.max(0, Math.floor(((v - origin) / size) * 3)));
  return REGION_GRID[third(point.y, ry, h)][third(point.x, rx, w)];
}

/**
 * The corner to ask for next (guided calibration R3), or why there is none.
 *
 * - Tiles are filled least-filled first. In each, the estimate is the tile's fit from four spots,
 *   or the first guess before then or after a refused fit.
 * - Candidates are the corners not placed and not skipped that the estimate puts in the tile: up to
 *   `FIRST_GUESS_VIEW_ALLOWANCE` beyond its edges on the first guess, `GUIDED_VIEW_MARGIN` inside
 *   them on a fit. Those in the camera's sight are offered first, then the rest; `bestNextTarget`
 *   chooses within each. (Asking for near corners first was measured and not shipped: it lowered
 *   the FTC-RED guided accuracy below its bar, findable-spots R2.)
 * - `held` is the corner offered last. While only the corners on offer changed (a later footprint
 *   read; Failure States: "the current target stays"), the caller passes it, and it is offered
 *   again for its tile so long as it is still open and the tile's estimate still puts it inside.
 *   A place, a skip or a refit is the caller's cue to pass null, so the choice is made afresh.
 * - `chosen` is a corner the person picked on the plan (findable-spots R4.1): while it is open it
 *   is the target, in the first tile, least-filled first, whose estimate puts it inside, else the
 *   least-filled tile.
 * - `passed` are corners Another corner moved past (R4.4): left out of the choice, but not set
 *   aside; once every open corner has been passed, the choice is made again over all of them.
 */
export function guidedTarget({
  draft,
  fits,
  corners,
  settled,
  marker,
  lens,
  layout,
  held = null,
  chosen = null,
  passed = [],
}: {
  draft: Pick<CalibrationDraft, 'pairs' | 'skipped' | 'spotsAsked' | 'mode'>;
  fits: ReadonlyArray<ViewFit>;
  corners: ReadonlyArray<GuidedCorner>;
  settled: boolean;
  marker: CalibrationMarker;
  lens: CalibrationLensChoice;
  layout: FloorPlannerCameraCalibrationLayout;
  held?: { key: string; view: number } | null;
  chosen?: string | null;
  passed?: ReadonlyArray<string>;
}): GuidedState {
  const asked = Math.max(MIN_GUIDED_SPOTS * Math.max(1, fits.length), draft.spotsAsked);
  // Every tile fitted on six spots or more: the run is done once the spots asked for are placed.
  // Short of that — a refused fit, or a tile the clicks left short — it keeps asking (Failure
  // States: a refusal's next target comes from the first guess).
  const complete = fits.length > 0 && fits.every(isSaveable);
  const wanted = complete ? asked : Math.max(asked, draft.pairs.length + 1);
  const none = (status: GuidedStatus): GuidedState => ({ status, target: null, wanted });
  if (draft.mode === 'saved') return none('saved');
  if (fits.length === 0) return none('reading');
  if (corners.length === 0) return none(settled ? 'nothing' : 'reading');
  if (draft.pairs.length >= wanted) return none('done');
  const taken = takenKeys(draft);
  const open = corners.filter(c => !taken.has(c.key));
  const estimateOf = (fit: ViewFit) => {
    const fitted = fit.verdict === 'ok' && fit.model !== null;
    return {
      fitted,
      estimate: fitted ? fit.model! : tileFirstGuess(marker, fit.rect, lens, layout, fit.index),
      margin: fitted ? GUIDED_VIEW_MARGIN : -FIRST_GUESS_VIEW_ALLOWANCE,
    };
  };
  const asking = (corner: GuidedCorner, fit: ViewFit, isChosen = false): GuidedState => {
    const { fitted, estimate } = estimateOf(fit);
    const q = planToPicture(estimate, corner.plan);
    const expected = q && Number.isFinite(q.x) && Number.isFinite(q.y) ? { x: q.x, y: q.y } : null;
    return {
      status: 'asking',
      target: { ...corner, view: fit.index, expected, fitted, chosen: isChosen },
      wanted,
    };
  };
  const order = [...fits].sort((a, b) => a.pairs.length - b.pairs.length || a.index - b.index);
  // The person's own pick (R4.1), while it is still open.
  const chosenCorner = chosen ? open.find(c => c.key === chosen) : undefined;
  if (chosenCorner) {
    const inside = order.find(fit => {
      const { estimate, margin } = estimateOf(fit);
      return projectsInsideView(estimate, chosenCorner.plan, fit.rect, margin);
    });
    return asking(chosenCorner, inside ?? order[0], true);
  }
  // The corner offered last stays while it is still open and still inside its tile.
  const heldFit = held ? fits.find(f => f.index === held.view) : undefined;
  const heldCorner = held ? open.find(c => c.key === held.key) : undefined;
  if (heldFit && heldCorner) {
    const { estimate, margin } = estimateOf(heldFit);
    if (projectsInsideView(estimate, heldCorner.plan, heldFit.rect, margin)) {
      return asking(heldCorner, heldFit);
    }
  }
  const choose = (available: ReadonlyArray<GuidedCorner>): GuidedState | null => {
    // Corners in the camera's sight first; the rest only once those are placed, skipped or out of
    // view.
    const inSight = available.filter(c => c.inSight !== false);
    const pools = [inSight, [...available]];
    for (const [fit, pool] of pools.flatMap(p => order.map(f => [f, p] as const))) {
      const { fitted, estimate, margin } = estimateOf(fit);
      const candidates = pool.filter(c => projectsInsideView(estimate, c.plan, fit.rect, margin));
      if (candidates.length === 0) continue;
      const pick = bestNextTarget(
        {
          rect: fit.rect,
          lens,
          layout,
          marker,
          pairs: fit.pairs.map(p => ({ picture: p.picture, plan: p.plan, source: p.source })),
        },
        estimate,
        candidates,
        fitted ? fit.observerHeight : null,
      ) as GuidedCorner | null;
      if (pick) return asking(pick, fit);
    }
    return null;
  };
  // Corners Another corner passed are left out until nothing else is left (R4.4).
  const skippedOver = new Set(passed);
  const found =
    (skippedOver.size > 0 ? choose(open.filter(c => !skippedOver.has(c.key))) : null) ??
    choose(open);
  // Settling may still add corners; until then there is nothing left to say is exhausted.
  return found ?? none(settled ? 'exhausted' : 'reading');
}
